import * as React from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Button } from '@/components/ui/button'
import { Download, Copy, Check } from 'lucide-react'

interface ChartData {
  label: string
  value: number
  category?: string | null
}

interface ChartArtifactProps {
  artifact: {
    chartType: 'bar' | 'line' | 'area' | 'pie' | 'donut'
    title: string
    data: ChartData[]
    xAxisLabel?: string | null
    yAxisLabel?: string | null
    showLegend?: boolean
    valuePrefix?: string | null
  }
  onExport?: (format: 'csv' | 'excel') => void
}

// Chart color palette
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16']

export function ChartArtifact({ artifact, onExport }: ChartArtifactProps) {
  const [copied, setCopied] = React.useState(false)
  const { chartType, title, data, xAxisLabel, yAxisLabel, showLegend, valuePrefix = 'RM ' } = artifact

  // Handle missing/empty data
  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="font-medium text-sm mb-2">{title}</h3>
        <p className="text-muted-foreground text-sm">No data available</p>
      </div>
    )
  }

  // Transform data for Recharts
  const chartData = data.map((d) => ({
    name: d.label,
    value: d.value,
    category: d.category || null,
  }))

  // Format value for display
  const formatValue = (v: number) => {
    const prefix = valuePrefix || ''
    if (v >= 1000000) return `${prefix}${(v / 1000000).toFixed(1)}M`
    if (v >= 1000) return `${prefix}${(v / 1000).toFixed(1)}K`
    return `${prefix}${v.toLocaleString()}`
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-sm text-emerald-500 font-semibold">
            {formatValue(payload[0].value)}
          </p>
        </div>
      )
    }
    return null
  }

  const handleCopy = async () => {
    const text = data.map((d) => `${d.label}: ${formatValue(d.value)}`).join('\n')
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        // Smart color assignment - detect comparison patterns
        const getBarColor = (item: { name: string; category: string | null }, index: number) => {
          const name = item.name.toLowerCase()
          const category = item.category?.toLowerCase() || ''

          // Month comparison (Nov vs Dec, etc)
          if (name.includes('nov') || category.includes('nov')) return '#3b82f6' // blue
          if (name.includes('dec') || category.includes('dec')) return '#10b981' // green
          if (name.includes('jan') || category.includes('jan')) return '#f59e0b' // amber
          if (name.includes('feb') || category.includes('feb')) return '#ef4444' // red

          // Year comparison
          if (name.includes('2024') || category.includes('2024')) return '#3b82f6' // blue
          if (name.includes('2025') || category.includes('2025')) return '#10b981' // green

          // New vs Existing
          if (name.includes('new') || category.includes('new')) return '#10b981' // green
          if (name.includes('existing') || category.includes('existing')) return '#3b82f6' // blue

          // Default: use color palette
          return COLORS[index % COLORS.length]
        }

        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                tickFormatter={formatValue}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {chartData.map((item, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(item, index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                tickFormatter={formatValue}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              <Line
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
                tickFormatter={formatValue}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              <Area
                type="monotone"
                dataKey="value"
                stroke="#06b6d4"
                fill="#06b6d4"
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        )

      case 'pie':
      case 'donut':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={chartType === 'donut' ? 60 : 0}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                labelLine={{ stroke: 'var(--color-muted-foreground)' }}
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatValue(value as number)}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
              {showLegend && <Legend />}
            </PieChart>
          </ResponsiveContainer>
        )

      default:
        return (
          <div className="text-center text-muted-foreground py-8">
            Unsupported chart type: {chartType}
          </div>
        )
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <h3 className="font-medium text-sm">{title}</h3>
        {(xAxisLabel || yAxisLabel) && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {xAxisLabel && `X: ${xAxisLabel}`}
            {xAxisLabel && yAxisLabel && ' | '}
            {yAxisLabel && `Y: ${yAxisLabel}`}
          </p>
        )}
      </div>

      {/* Chart */}
      <div className="p-4">{renderChart()}</div>

      {/* Actions */}
      <div className="flex gap-2 px-4 py-3 border-t border-border bg-muted/20">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onExport?.('csv')}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onExport?.('excel')}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Excel
        </Button>
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? (
            <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5 mr-1.5" />
          )}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}
